import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import Products from 'merchandise-lib/constants/Product';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { CORE_SEATS_ENTERPRISE, CORE_SEATS_PROFESSIONAL } from 'self-service-api/constants/ApiNames';
import { ENTERPRISE, PROFESSIONAL } from 'self-service-api/constants/ProductLevels';
import BannerSection from '../components/banners/BannerSection';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import SmartCrmFeatureTable from '../components/feature-table/smart-crm-feature-table/SmartCrmFeatureTable';
import { ProductCard } from '../components/merch-card/cards/product-card/ProductCard';
import { getI18nKeyOverrideForProductCard } from '../components/merch-card/I18nUtils';
import MobileCardWrapper from '../components/mobile/containers/MobileCardWrapper';
import MobileWrapper from '../components/mobile/containers/MobileWrapper';
import MobileMerchCardSelectorButtons from '../components/mobile/MobileMerchCardSelectorButtons';
import { SMART_CRM_PAGE, SMART_CRM_PAGE_CONTENT } from '../constants/ErrorBoundaryNames';
import { STANDALONE_CORE_SEATS } from '../constants/ReleaseInsights';
import { NavItemEnum } from '../constants/routing/NavItems';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useHasReleaseInsight } from '../hooks/useBulkPurchaseOptions';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useQueryParamsEffect, useQueryParamTerm } from '../utils/urls/appQueryParamUtilsLegacy';
import { ErrorBoundary } from '../v2/components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from '../v2/components/error-boundary/ErrorSeverity';
import withErrorBoundary from '../v2/components/error-boundary/withErrorBoundary';
import PhoneSupportFooter from '../v2/components/phone-support/PhoneSupportFooter';
import { SCREENS } from '../v2/constants/tracking/Screens';
import { SMART_CRM_CARD } from '../v2/constants/tracking/TrackingSources';
import { IntegrationsSection } from '../v2/containers/IntegrationsSection';
import { trackPageView } from '../v2/utils/tracking/trackingEventUtils';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PRODUCT_LEVELS_MAP = {
  [CORE_SEATS_PROFESSIONAL]: PROFESSIONAL,
  [CORE_SEATS_ENTERPRISE]: ENTERPRISE
};
const PUBLIC_PRODUCTS_TO_SHOW = [CORE_SEATS_PROFESSIONAL, CORE_SEATS_ENTERPRISE];
const MobileSmartCrmPage = () => {
  useAnchorLinkScroll();
  useDocumentTitle('smart-crm');
  const screen = SCREENS.SMART_CRM;
  const navItem = NavItemEnum.SMART_CRM;
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const isAppReady = useGlobalLoading();
  const {
    hasReleaseInsight: isUngatedForSmartCrm,
    loading: isLoadingReleaseInsight,
    error: isErrorLoadingReleaseInsight
  } = useHasReleaseInsight({
    releaseInsight: STANDALONE_CORE_SEATS
  });
  const productsToShow = PUBLIC_PRODUCTS_TO_SHOW;
  const productLevelsToShow = productsToShow.map(apiName => PRODUCT_LEVELS_MAP[apiName]);
  const [selectedProductLevel, setSelectedProductLevel] = useState(productLevelsToShow[0]);
  const [selectedProduct, setSelectedProduct] = useState(productsToShow[0]);
  const redirectCondition = () => {
    if (isLoadingReleaseInsight) return;
    return !isUngatedForSmartCrm || !!isErrorLoadingReleaseInsight;
  };
  useRedirectIfIneligible({
    condition: redirectCondition
  });
  useEffect(() => {
    if (!isAppReady) return;
    trackPageView({
      screen
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppReady]);
  const onClickProductLevelSelectorButton = productLevel => {
    setSelectedProductLevel(productLevel);
    setSelectedProduct(productsToShow[productLevelsToShow.indexOf(productLevel)]);
  };
  if (!isAppReady) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  const i18nKeys = getI18nKeyOverrideForProductCard(selectedProduct);
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
      navItem: navItem
    }),
    errorBoundaryName: SMART_CRM_PAGE_CONTENT,
    severity: ERROR_SEVERITY.HIGH,
    metricsOptions: {
      route: navItem
    },
    children: /*#__PURE__*/_jsxs(MobileWrapper, {
      navItem: navItem,
      navMarkerName: "SMART_CRM_PAGE",
      children: [/*#__PURE__*/_jsx(BannerSection, {
        screen: screen
      }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
        children: [/*#__PURE__*/_jsx(ProductPageTitle, {
          nonProductIdentifier: navItem
        }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
          nonProductIdentifier: navItem
        })]
      }), /*#__PURE__*/_jsxs(PageSection, {
        children: [/*#__PURE__*/_jsx(MobileMerchCardSelectorButtons, {
          options: productLevelsToShow,
          onClick: onClickProductLevelSelectorButton,
          selectedOption: selectedProductLevel
        }), /*#__PURE__*/_jsx(MobileCardWrapper, {
          children: /*#__PURE__*/_jsx(ProductCard, {
            apiName: selectedProduct,
            screen: screen,
            trackingSource: SMART_CRM_CARD,
            columnStart: 1,
            Title: i18nKeys === null || i18nKeys === void 0 ? void 0 : i18nKeys.Title,
            Description: i18nKeys === null || i18nKeys === void 0 ? void 0 : i18nKeys.Description
          })
        }), /*#__PURE__*/_jsx(SmartCrmFeatureTable, {
          productLevels: [selectedProductLevel],
          screen: screen
        })]
      }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(PhoneSupportFooter, {
        phoneSourceId: "smart-crm-footer",
        screen: screen,
        apiName: Products.CORE_SEATS_PROFESSIONAL
      })]
    })
  });
};
export default withErrorBoundary(MobileSmartCrmPage, {
  fallback: /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  }),
  errorBoundaryName: SMART_CRM_PAGE,
  severity: ERROR_SEVERITY.HIGH
});